import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "@/App";
import { Calculator, Plus, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const grades = ["S", "A", "B", "C", "D", "E", "F"];
const gradeColors = {
  S: "text-emerald-400",
  A: "text-cyan-400",
  B: "text-blue-400",
  C: "text-violet-400",
  D: "text-amber-400",
  E: "text-orange-400",
  F: "text-red-400",
};

export default function CGPACalculatorPage() {
  const [subjects, setSubjects] = useState([
    { name: "Subject 1", grade: "A", credits: 4 },
    { name: "Subject 2", grade: "B", credits: 4 },
    { name: "Subject 3", grade: "A", credits: 3 },
  ]);
  const [result, setResult] = useState(null);

  const addSubject = () => {
    setSubjects([...subjects, { name: `Subject ${subjects.length + 1}`, grade: "A", credits: 4 }]);
  };

  const removeSubject = (index) => {
    if (subjects.length > 1) {
      setSubjects(subjects.filter((_, i) => i !== index));
    }
  };

  const updateSubject = (index, field, value) => {
    const updated = [...subjects];
    updated[index][field] = value;
    setSubjects(updated);
  };

  const calculate = async () => {
    try {
      const res = await axios.post(`${API}/cgpa/calculate`, { subjects });
      setResult(res.data);
      toast.success(`Your CGPA: ${res.data.cgpa}`);
    } catch {
      toast.error("Calculation failed");
    }
  };

  return (
    <div data-testid="cgpa-calculator-page" className="min-h-screen pt-24 pb-12">
      <div className="absolute top-0 left-0 w-full h-[400px] bg-gradient-to-b from-indigo-600/5 to-transparent pointer-events-none" />

      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" animate="visible" variants={stagger}>
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <div className="inline-flex items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 p-4 mb-6">
              <Calculator className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-heading text-4xl md:text-6xl font-bold tracking-tight text-white mb-4">CGPA Calculator</h1>
            <p className="text-zinc-500">Calculate your VTU CGPA instantly with the grade point system.</p>
          </motion.div>

          {/* Calculator Panel */}
          <motion.div
            variants={fadeInUp}
            className="rounded-3xl bg-black/40 border border-white/10 backdrop-blur-xl p-6 sm:p-8"
          >
            {/* Headers */}
            <div className="grid grid-cols-12 gap-3 mb-4 px-1">
              <div className="col-span-5 text-xs font-medium tracking-widest text-zinc-500 uppercase">Subject</div>
              <div className="col-span-3 text-xs font-medium tracking-widest text-zinc-500 uppercase">Grade</div>
              <div className="col-span-3 text-xs font-medium tracking-widest text-zinc-500 uppercase">Credits</div>
              <div className="col-span-1" />
            </div>

            {/* Subject Rows */}
            <div className="space-y-3">
              {subjects.map((sub, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="grid grid-cols-12 gap-3 items-center"
                >
                  <div className="col-span-5">
                    <input
                      data-testid={`subject-name-${i}`}
                      type="text"
                      value={sub.name}
                      onChange={(e) => updateSubject(i, "name", e.target.value)}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-violet-500/50 transition-colors"
                    />
                  </div>
                  <div className="col-span-3">
                    <Select value={sub.grade} onValueChange={(v) => updateSubject(i, "grade", v)}>
                      <SelectTrigger data-testid={`subject-grade-${i}`} className="bg-white/5 border-white/10 text-white rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-white/10">
                        {grades.map((g) => (
                          <SelectItem key={g} value={g} className={`${gradeColors[g]}`}>{g} ({g === "S" ? 10 : g === "A" ? 9 : g === "B" ? 8 : g === "C" ? 7 : g === "D" ? 6 : g === "E" ? 5 : 0})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3">
                    <input
                      data-testid={`subject-credits-${i}`}
                      type="number"
                      min="1"
                      max="10"
                      value={sub.credits}
                      onChange={(e) => updateSubject(i, "credits", parseInt(e.target.value) || 0)}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white outline-none focus:border-violet-500/50 transition-colors"
                    />
                  </div>
                  <div className="col-span-1 flex justify-center">
                    <button
                      data-testid={`remove-subject-${i}`}
                      onClick={() => removeSubject(i)}
                      className="p-2 rounded-lg text-zinc-600 hover:text-red-400 hover:bg-red-400/10 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Add Subject */}
            <button
              data-testid="add-subject-btn"
              onClick={addSubject}
              className="mt-4 flex items-center gap-2 text-sm text-zinc-500 hover:text-violet-400 transition-colors px-1"
            >
              <Plus className="w-4 h-4" /> Add Subject
            </button>

            {/* Calculate */}
            <div className="mt-8 flex flex-col items-center gap-6">
              <button
                data-testid="calculate-cgpa-btn"
                onClick={calculate}
                className="w-full sm:w-auto px-10 py-3.5 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold hover:shadow-[0_0_30px_rgba(139,92,246,0.4)] active:scale-95 transition-transform flex items-center justify-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                Calculate CGPA
              </button>

              {/* Result */}
              {result && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  data-testid="cgpa-result"
                  className="text-center p-8 rounded-2xl bg-gradient-to-br from-violet-600/10 to-cyan-600/10 border border-white/10"
                >
                  <div className="text-xs font-medium tracking-widest text-zinc-500 uppercase mb-2">Your CGPA</div>
                  <div className="font-heading text-6xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-violet-400 to-cyan-400">
                    {result.cgpa}
                  </div>
                  <div className="text-sm text-zinc-500 mt-2">Total Credits: {result.total_credits}</div>
                </motion.div>
              )}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
